import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    ListSubheader,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import { JSX, useCallback, useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { fieldProps, useValidatedForm } from '../../../library/formValidation';
import { DISCOUNT_PERCENT_BOUNDS, numericSlotProps } from '../../../library/validationRules';
import { useApiFormError } from '../../../library/useApiFormError';
import { toApiTime, toDateInputValue, toDateTimeInputValue } from '../../../library/dateTimeHelper';
import { CreatePurchaseRequest, IPatientPurchase } from '../../../library/models/patientPurchase';
import { IService } from '../../../library/models/service';
import { localised } from '../../../library/models/serviceCatalog';
import { formatPrice, priceBreakdown } from '../../../library/purchaseDisplay';
import { ITherapist } from '../../../library/models/therapist';
import { useIsCompact } from '../../../library/useIsCompact';
import ResourcesIds from '../../../resources/ResourcesIds';
import { AppDatePicker } from '../../pickers/AppDatePicker';
import { AppTimePicker } from '../../pickers/AppTimePicker';
import { useAuth } from '../../../contexts/AuthContext';
import { useReactMutation, useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import { useSnackbar } from '../../layout/snackbarProvider';
import { AddPurchaseFormValues, createAddPurchaseSchema } from './addPurchaseSchema';
import {
    derivedPriceStyles,
    descriptionStyles,
    dialogActionsStyles,
    dialogFormStyles,
    dialogTitleStyles,
    fieldsBoxStyles,
    formAlertStyles,
    perSessionStyles,
    priceRowStyles,
    sessionBlockStyles,
    sessionHintStyles,
    sessionTimeStyles,
    twoUpItemStyles,
    twoUpStyles,
} from './addPurchaseDialog.styles';

/**
 * Opening the sale with terms already on it, for a session being sold as it ends.
 *
 * The appointment card sends one: the therapist pressed *Mark as held* on a session that
 * is already in the diary, so which service, whose it is and what day it was are all
 * settled -- only the money is still a question. Everything stays editable; these are
 * starting values, exactly as picking a service by hand produces.
 *
 * <b>Its presence means more than its contents.</b> A prefill is what unticks *record the
 * session*, because a prefilled sale is by definition one for an appointment that already
 * exists. So a caller with nothing but the day and the therapist to offer still passes one
 * -- an empty prefill is a statement, not an omission.
 */
export interface AddPurchasePrefill {
    /**
     * A catalogue option id. Fills the service, its price and its session count.
     *
     * Absent when the appointment names no service the price list still holds -- it was
     * pruned, or the row never carried one. The service is then picked by hand, and
     * everything else about the prefill still applies.
     */
    serviceId?: number;
    therapistId?: number;
    /** `yyyy-MM-dd`. The day the session happened, rather than today. */
    soldOn?: string;
}

interface AddPurchaseDialogProps {
    open: boolean;
    patientId: number;
    onClose: () => void;
    /**
     * The patient's list has to pick up the new purchase -- and the appointment card has
     * to know which one to settle against, which is why the row is handed over rather
     * than merely announced.
     */
    onAdded: (purchase: IPatientPurchase) => void;
    /** Set when the sale is being opened for a session that already exists. See above. */
    prefill?: AddPurchasePrefill;
}

/** "13:30" for right now, which is what a session sold as it ends started near. */
const timeNow = (): string => toDateTimeInputValue(new Date()).slice(11);

/**
 * A function rather than a constant: the date and time both default to now, and a
 * constant computed at import time would still be saying yesterday tomorrow morning.
 */
const defaultValues = (): AddPurchaseFormValues => ({
    serviceId: '',
    therapistId: '',
    totalSessions: '1',
    listPrice: '',
    discountPercent: '0',
    soldOn: toDateInputValue(new Date()),
    // Ticked by default because the common sale is the one made at the desk as the
    // patient leaves. A session booked in advance is the other case, and unticking says so.
    sessionHeld: true,
    providedAtTime: timeNow(),
    validUntil: '',
    notes: '',
});

/** Two decimals, as a string, since every number in this form is one. */
const money = (amount: number): string => (Math.round(amount * 100) / 100).toFixed(2);

/**
 * Sells a patient a service.
 *
 * The service is chosen from the clinic's list, but nothing is bound to it afterwards:
 * the therapist may change the session count, take something off the price, or set a
 * date, and what the server stores is a snapshot of those terms. Editing the catalogue
 * later cannot reach back and change what this patient paid.
 *
 * The dates depend on what is being sold. A package is bought now and drawn down later,
 * so it asks how long it stays good for. A single session is usually sold at the desk as
 * the patient leaves, so it asks the day and the hour and records the session itself --
 * one act rather than two, and the diary then holds a real appointment at a real time
 * instead of a card claiming a delivery nothing in the diary agrees happened.
 *
 * The tick is what makes that honest rather than merely convenient. Unticked, the sale is
 * only a sale, which is what a session already booked in needs: that appointment exists
 * and names this purchase when it is marked held. Writing a second one would double-count
 * the session, and the overlap guard would refuse the sale outright for good measure.
 *
 * The final price is read, not typed. It is the list price less the discount, and a field
 * a therapist can type into is a third statement of a sum that already has two -- one that
 * can be left saying something the other two do not. "Call it 900" is still said out loud
 * and still entered, by moving the *list* price, which is the figure the clinic actually
 * controls. Only the discount as an amount is sent; the price the patient pays is worked out
 * again on the server, which is the copy that counts.
 */
export const AddPurchaseDialog = (props: AddPurchaseDialogProps): JSX.Element => {
    const { prefill } = props;
    const { i18n, t: translate } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const { user } = useAuth();
    const isCompact = useIsCompact();

    const language: string = i18n.language;

    const schema = useMemo(() => createAddPurchaseSchema(translate), [translate]);

    const {
        register,
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        setError,
        formState: { errors, isValid, isSubmitting },
    } = useValidatedForm<AddPurchaseFormValues>(schema, { defaultValues: defaultValues() });

    const knownFields: string[] = useMemo(() => Object.keys(schema.fields), [schema]);
    const { formError, report, clear } = useApiFormError<AddPurchaseFormValues>(
        setError, knownFields, translate, ResourcesIds.ADD_PURCHASE_ERROR);

    // 'ALL' because this dialog sells both kinds. The booking wizard's call passes
    // nothing and gets single sessions only.
    const { data: serviceData, isLoading: isLoadingServices } = useReactQuery<IService[]>(
        services.services.getServices('ALL'),
        { enabled: props.open }
    );

    const createPurchase = useReactMutation(services.patientPurchases.createPurchaseMutation());

    const availableServices = useMemo(() => serviceData ?? [], [serviceData]);

    /**
     * What may pay for the session this sale was opened from: **that service, or a block of
     * it.** Nothing else in the price list is an answer.
     *
     * The rest of the catalogue is not merely further down the list, it is absent. A sale
     * opened from an appointment is settling *this* session, so a prenatal massage or a
     * Kinetoterapie package is the same wrong answer the appointment card stopped offering
     * one step earlier -- it would be a different sale, made against the wrong session.
     *
     * Which package is a block of this session is a question only the catalogue link can
     * answer; before it, the therapist read the whole price list to find one.
     *
     * Headings only when there is more than one group to tell apart, the rule the
     * appointment card and the booking dialog both follow -- so a service with no packages
     * behind it is one unlabelled line rather than a heading over a list of one.
     */
    const serviceGroups = useMemo(() => {
        const wholeList = [{ heading: null, services: availableServices }];

        if (!prefill?.serviceId) {
            return wholeList;
        }

        const groups = [
            {
                heading: ResourcesIds.ADD_PURCHASE_GROUP_THIS_SESSION,
                services: availableServices.filter(one => one.id === prefill.serviceId),
            },
            {
                heading: ResourcesIds.ADD_PURCHASE_GROUP_PACKAGES,
                services: availableServices.filter(
                    one => one.basedOnOptionId === prefill.serviceId),
            },
        ].filter(group => group.services.length > 0);

        // Narrowing to nothing would stand the therapist in front of an empty dropdown with
        // a session still to settle. It should not be reachable -- the card sends no service
        // when the catalogue no longer holds one -- so this is the honest fallback for a
        // state that would otherwise be a dead end rather than a case worth designing for.
        return groups.length > 0 ? groups : wholeList;
    }, [availableServices, prefill]);

    const selectedServiceId = watch('serviceId');
    const selectedTherapistId = watch('therapistId');
    const totalSessions = watch('totalSessions');
    const listPrice = watch('listPrice');
    const discountPercentValue = watch('discountPercent');
    const sessionHeld = watch('sessionHeld');

    // Derived on every render rather than held in a fourth field: two numbers are typed and
    // the third is what they come to. `discountAmount` is the one that goes on the wire and
    // `finalPrice` the one on screen, both out of the same call, so the total the therapist
    // reads is the total the row will hold.
    const { discountAmount, finalPrice } = priceBreakdown(
        Number(listPrice), Number(discountPercentValue));

    // Who can give the chosen service -- the same lookup the booking wizard uses, since
    // "who provides this" is exactly what it already answers.
    const { data: therapistData, isLoading: isLoadingTherapists } = useReactQuery<ITherapist[]>(
        services.therapists.getTherapists(Number(selectedServiceId)),
        { enabled: props.open && !!selectedServiceId }
    );

    const availableTherapists = useMemo(() => therapistData ?? [], [therapistData]);

    // Defaults to whoever is signed in, but only once they are known to actually
    // practise the chosen service -- otherwise the field is left for an explicit pick,
    // which the schema requires before the form can be submitted.
    useEffect(() => {
        if (selectedTherapistId || availableTherapists.length === 0) {
            return;
        }

        // The appointment's own therapist leads, when the clinic agrees they give this --
        // they are the person who actually gave the session being sold. Applied here
        // rather than in the reset because it has to be one of the options this select is
        // showing, and that list is fetched for the chosen service.
        const preferred = [prefill?.therapistId, user?.id]
            .find(id => id != null && availableTherapists.some(therapist => therapist.id === id));

        if (preferred != null) {
            setValue('therapistId', String(preferred), { shouldValidate: true });
        }
    }, [user, prefill, selectedTherapistId, availableTherapists, setValue]);

    const selectedService = useMemo(
        () => availableServices.find(service => String(service.id) === selectedServiceId),
        [availableServices, selectedServiceId]
    );

    // Nothing picked yet reads as a single session, which is what the rest of the form
    // already assumes -- the session count starts at one and stays locked until a package
    // is chosen.
    const isPackage: boolean = selectedService?.serviceType === 'PACKAGE';

    /*
     * Cleared on every open, so the last sale's terms are never applied to the next one --
     * and refilled from the prefill in the same breath rather than by a later effect,
     * which this reset would simply undo.
     *
     * Only what the caller actually knows is set here. The service's own terms wait for
     * the catalogue below, since they are read out of a list that is still loading.
     */
    useEffect(() => {
        if (!props.open) {
            return;
        }

        reset({
            ...defaultValues(),
            ...(prefill
                ? {
                    ...(prefill.serviceId ? { serviceId: String(prefill.serviceId) } : {}),
                    soldOn: prefill.soldOn ?? toDateInputValue(new Date()),
                    // The session is already in the diary; this sale must not write a second.
                    // True of any prefill, including one that names no service.
                    sessionHeld: false,
                }
                : {}),
        });
        clear();
    }, [props.open, prefill, reset, clear]);

    /**
     * Picking a service fills in its published terms. They are starting values rather
     * than fixed ones: everything below stays editable, and whatever ends up in the
     * fields is what gets stored.
     */
    const handleServiceChange = (serviceId: string): void => {
        setValue('serviceId', serviceId, { shouldValidate: true });

        // A different service is a different discipline, so whoever was picked to
        // provide the last one is not assumed to provide this one -- the effect above
        // fills it back in once the new list confirms the signed-in therapist is on it.
        setValue('therapistId', '');

        applyServiceTerms(serviceId);
    };

    /**
     * A service's published terms, written into the fields below it.
     *
     * Its own function because two things fill this form: the select, and a prefill from
     * the appointment card. The card's sale has to land on the same terms a therapist
     * picking that service by hand would have seen, and two spellings of "what does this
     * service cost" would drift the first time either changed.
     */
    const applyServiceTerms = useCallback((serviceId: string): void => {
        const service = availableServices.find(candidate => String(candidate.id) === serviceId);

        if (!service) {
            return;
        }

        setValue('totalSessions', String(service.sessionCount));
        setValue('listPrice', money(service.price));
        setValue('discountPercent', '0');

        // Kept in step with what is on screen rather than left where the last service put
        // it: a package is drawn down over weeks, so it has no one moment it was given and
        // the box is not shown for one. Left ticked out of sight, it would demand a time
        // the form no longer displays and the submit would fail with nothing to point at.
        //
        // A prefilled sale never ticks it whatever the service is: the session it is being
        // sold for is already in the diary, and a second HELD row would double-count it --
        // the overlap guard would refuse the sale outright for good measure.
        setValue('sessionHeld', !prefill && service.serviceType !== 'PACKAGE',
            { shouldValidate: true });
    }, [availableServices, prefill, setValue]);

    /*
     * The prefilled service's price and session count, once the catalogue has arrived.
     *
     * Guarded on the list rather than on a ref: the reset above runs before the services
     * are known, so the terms cannot be written with it, and `applyServiceTerms` is a
     * no-op until the option it names is actually there. A therapist who has since edited
     * the price is not overwritten, because this only ever runs on an open.
     */
    useEffect(() => {
        if (props.open && prefill && availableServices.length > 0) {
            applyServiceTerms(String(prefill.serviceId));
        }
    }, [props.open, prefill, availableServices, applyServiceTerms]);

    /**
     * Whether this sale is also the record of the session.
     *
     * The tick alone is not enough: it is only shown for a single session, and a package
     * is drawn down over weeks rather than given at one hour -- the API refuses a time on
     * one, so sending a stale tick would turn a valid sale into a 400.
     */
    const wantsSession = (values: AddPurchaseFormValues): boolean =>
        !isPackage && values.sessionHeld;

    const toRequest = (values: AddPurchaseFormValues): CreatePurchaseRequest => {
        const terms = priceBreakdown(Number(values.listPrice), Number(values.discountPercent));

        return {
            patientId: props.patientId,
            serviceId: Number(values.serviceId),
            therapistId: Number(values.therapistId),
            totalSessions: Number(values.totalSessions),
            listPrice: Number(values.listPrice),
            // The amount, not the percentage: that is what the row stores, and it is the same
            // figure the field above worked the displayed total out from -- so the number the
            // therapist read out loud is the number the server subtracts.
            discountAmount: terms.discountAmount,
            // Only the one that was on screen. The other field keeps whatever was typed
            // into it before the service was changed, and sending that would record a
            // term the therapist was never shown.
            assignedOn: isPackage ? undefined : values.soldOn || undefined,
            // The hour turns this sale into the record of the session as well: the API
            // writes it into the diary as already held. Left out, the sale is only a sale
            // and the session is whatever is already booked in.
            providedAt: wantsSession(values) ? toApiTime(values.providedAtTime) : undefined,
            validUntil: isPackage ? values.validUntil || undefined : undefined,
            notes: values.notes || undefined,
        };
    };

    const onValid = (values: AddPurchaseFormValues): void => {
        clear();

        createPurchase.mutate(toRequest(values), {
            onSuccess: (created: IPatientPurchase) => {
                showSnackbar(translate(ResourcesIds.ADD_PURCHASE_SUCCESS), 'success');
                props.onAdded(created);
                props.onClose();
            },
            // A per-field rejection now lands on the field it names rather than being
            // flattened into the banner.
            onError: report,
        });
    };

    const sessions = Number(totalSessions);
    const showPerSession = isPackage && sessions > 0;

    return (
        <Dialog open={props.open} onClose={props.onClose} maxWidth="sm" fullWidth fullScreen={isCompact}>
            <DialogTitle sx={dialogTitleStyles}>
                {translate(ResourcesIds.ADD_PURCHASE_TITLE)}
            </DialogTitle>

            <Box component="form" onSubmit={handleSubmit(onValid)} noValidate sx={dialogFormStyles}>
                <DialogContent>
                    {formError && (
                        <Alert severity="error" sx={formAlertStyles}>
                            {formError}
                        </Alert>
                    )}

                    {!isLoadingServices && serviceData === undefined && (
                        <Alert severity="error" sx={formAlertStyles}>
                            {translate(ResourcesIds.ADD_PURCHASE_SERVICES_LOAD_ERROR)}
                        </Alert>
                    )}

                    <Typography variant="body2" sx={descriptionStyles}>
                        {translate(ResourcesIds.ADD_PURCHASE_SUBTITLE)}
                    </Typography>

                    <Box sx={fieldsBoxStyles}>
                        <TextField
                            select
                            fullWidth
                            value={selectedServiceId}
                            onChange={event => handleServiceChange(event.target.value)}
                            label={translate(ResourcesIds.ADD_PURCHASE_SERVICE_LABEL)}
                            {...fieldProps(errors, 'serviceId')}
                            disabled={isLoadingServices}
                        >
            {/* The duration reads off the catalogue's own label rather than
                                being composed from the numbers: the clinic wrote "regiune"
                                and "20 min (membru superior)" for a reason, and those say
                                more than the minutes behind them could. */}
                            {serviceGroups.flatMap(group => [
                                ...(group.heading && serviceGroups.length > 1
                                    ? [
                                        <ListSubheader key={group.heading}>
                                            {translate(group.heading)}
                                        </ListSubheader>,
                                    ]
                                    : []),
                                ...group.services.map(service => (
                                    <MenuItem key={service.id} value={String(service.id)}>
                                        {localised(service.nameRo, service.nameEn, language)}
                                        {` — ${localised(service.durationLabelRo, service.durationLabelEn, language)}`}
                                        {` — ${formatPrice(service.price)} RON`}
                                    </MenuItem>
                                )),
                            ])}
                        </TextField>

                        {/* Who provides it, not who is selling it -- the list is the same
                            one the booking wizard offers, since both ask "who practises
                            this discipline". Disabled until a service names a discipline
                            to ask that question about. */}
                        <TextField
                            select
                            fullWidth
                            value={selectedTherapistId}
                            onChange={event => setValue('therapistId', event.target.value, { shouldValidate: true })}
                            label={translate(ResourcesIds.ADD_PURCHASE_THERAPIST_LABEL)}
                            {...fieldProps(errors, 'therapistId')}
                            disabled={!selectedServiceId || isLoadingTherapists}
                        >
                            {availableTherapists.map(therapist => (
                                <MenuItem key={therapist.id} value={String(therapist.id)}>
                                    {therapist.firstName} {therapist.lastName}
                                </MenuItem>
                            ))}
                        </TextField>

                        <Box sx={twoUpStyles}>
                            <TextField
                                {...register('totalSessions')}
                                sx={twoUpItemStyles}
                                type="number"
                                label={translate(ResourcesIds.ADD_PURCHASE_SESSIONS_LABEL)}
                                {...fieldProps(errors, 'totalSessions')}
                                // A single session is one session. Selling ten of them is
                                // what a package is, and the API refuses it anyway.
                                disabled={!isPackage}
                                slotProps={{ inputLabel: { shrink: true } }}
                            />

                            {/* A package is bought now and used over the weeks that follow,
                                so the date that matters is how long it stays good for. A
                                single session is one thing that happened on one day, and
                                an expiry for it would be a date nobody could answer. */}
                            {isPackage ? (
                                <Controller
                                    name="validUntil"
                                    control={control}
                                    render={({ field }) => (
                                        <AppDatePicker
                                            label={translate(
                                                ResourcesIds.ADD_PURCHASE_VALID_UNTIL_LABEL
                                            )}
                                            value={field.value}
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            name={field.name}
                                            inputRef={field.ref}
                                            sx={twoUpItemStyles}
                                            // A package sold today cannot already have
                                            // expired. The schema says the same thing, for
                                            // a date typed into the field rather than
                                            // picked out of the calendar.
                                            minDate={toDateInputValue(new Date())}
                                            {...fieldProps(errors, 'validUntil', translate(
                                                ResourcesIds.ADD_PURCHASE_VALID_UNTIL_HINT))}
                                        />
                                    )}
                                />
                            ) : (
                                <Controller
                                    name="soldOn"
                                    control={control}
                                    render={({ field }) => (
                                        <AppDatePicker
                                            label={translate(
                                                ResourcesIds.ADD_PURCHASE_SOLD_ON_LABEL
                                            )}
                                            value={field.value}
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            name={field.name}
                                            inputRef={field.ref}
                                            sx={twoUpItemStyles}
                                            // A sale cannot have happened tomorrow.
                                            disableFuture
                                            {...fieldProps(errors, 'soldOn')}
                                        />
                                    )}
                                />
                            )}
                        </Box>

                        {/* Selling a single session and recording it are usually one act,
                            so the form does both and asks for the hour it needs to say so.
                            The alternative is a sale that claims a session happened with no
                            appointment behind it -- which is what a therapist is looking at
                            when a card says both "given on the 10th" and "not held yet".

                            Unticked for the session already in the diary: that appointment
                            exists, and marking it held is what names this purchase. */}
                        {!isPackage && !prefill && (
                            <Box sx={sessionBlockStyles}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            {...register('sessionHeld')}
                                            checked={sessionHeld}
                                        />
                                    }
                                    label={translate(ResourcesIds.ADD_PURCHASE_SESSION_HELD_LABEL)}
                                />

                                <Typography variant="body2" sx={sessionHintStyles}>
                                    {translate(ResourcesIds.ADD_PURCHASE_SESSION_HELD_HINT)}
                                </Typography>

                                {sessionHeld && (
                                    <Controller
                                        name="providedAtTime"
                                        control={control}
                                        render={({ field }) => (
                                            <AppTimePicker
                                                label={translate(
                                                    ResourcesIds.ADD_PURCHASE_PROVIDED_AT_LABEL
                                                )}
                                                value={field.value}
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                                name={field.name}
                                                inputRef={field.ref}
                                                sx={sessionTimeStyles}
                                                {...fieldProps(errors, 'providedAtTime')}
                                            />
                                        )}
                                    />
                                )}
                            </Box>
                        )}

                        {/* Every field below is filled programmatically when a service is
                            picked, and MUI only floats a label for a value it saw arrive
                            through React. These arrive via setValue, so the label has to
                            be told to shrink -- otherwise it sits on top of the number. */}
                        <Box sx={priceRowStyles}>
                            <TextField
                                {...register('listPrice')}
                                fullWidth
                                type="number"
                                label={translate(ResourcesIds.ADD_PURCHASE_LIST_PRICE_LABEL)}
                                {...fieldProps(errors, 'listPrice')}
                                // No shared bound: the schema sets no ceiling on what a
                                // clinic may charge, and inventing one here would be a rule
                                // nobody wrote. The floor mirrors the rule that does exist.
                                slotProps={{
                                    htmlInput: { min: 0, step: 0.01 },
                                    inputLabel: { shrink: true },
                                }}
                            />

                            <TextField
                                {...register('discountPercent')}
                                fullWidth
                                type="number"
                                label={translate(
                                    ResourcesIds.ADD_PURCHASE_DISCOUNT_PERCENT_LABEL
                                )}
                                {...fieldProps(errors, 'discountPercent')}
                                // The spinner and the schema read one declaration, so the
                                // arrows cannot offer a percentage the form then refuses --
                                // which is how this field came to accept a negative one.
                                slotProps={{
                                    ...numericSlotProps(DISCOUNT_PERCENT_BOUNDS),
                                    inputLabel: { shrink: true },
                                }}
                            />

                            {/* Read, not typed: the total is the two fields to its left, and
                                a third box a therapist can disagree with them in is a bug
                                waiting to be filed. `readOnly` rather than `disabled`,
                                because this is the number that gets read out at the desk and
                                disabled ink is too faint to read across one. */}
                            <TextField
                                fullWidth
                                sx={derivedPriceStyles}
                                // `number` like the two it is derived from, even though it
                                // cannot be typed into: Chrome writes a number input's
                                // decimal separator the way the locale does, so a text field
                                // beside them read `660.00` against their `660,00`.
                                type="number"
                                value={formatPrice(finalPrice)}
                                label={translate(ResourcesIds.ADD_PURCHASE_FINAL_PRICE_LABEL)}
                                slotProps={{
                                    input: { readOnly: true },
                                    // Out of the tab order as well as out of the mouse's
                                    // reach -- a box the keyboard lands in is one a therapist
                                    // will try to type in.
                                    htmlInput: { tabIndex: -1 },
                                    inputLabel: { shrink: true },
                                }}
                            />
                        </Box>

                        {showPerSession && (
                            <Typography variant="body2" sx={perSessionStyles}>
                                {translate(ResourcesIds.PURCHASE_PER_SESSION, {
                                    price: money(finalPrice / sessions),
                                })}
                            </Typography>
                        )}

                        <TextField
                            {...register('notes')}
                            fullWidth
                            multiline
                            minRows={2}
                            label={translate(ResourcesIds.ADD_PURCHASE_NOTES_LABEL)}
                            {...fieldProps(errors, 'notes')}
                        />
                    </Box>
                </DialogContent>

                <DialogActions sx={dialogActionsStyles}>
                    <Button onClick={props.onClose} variant="outlined" disabled={isSubmitting}>
                        {translate(ResourcesIds.CANCEL_TEXT)}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={!isValid || isSubmitting || createPurchase.isPending}
                    >
                        {translate(ResourcesIds.ADD_PURCHASE_SUBMIT)}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};
